import { useEffect, useMemo } from 'react';

import type { ProjectGroupV1SchemaType } from '@/global/schemas/project.schema';
import { useFetchProjectGroupsV1Query, useFetchProjectWalletV1Query } from '@/store/services/project.service';
import { useAppSelector } from '@/store/utils';

import type { ProjectGroupV1Hook } from '../types/project.type';
import { useWeb3 } from '../useWeb3';

const useProjectGroupV1 = (): ProjectGroupV1Hook => {
  const { address } = useWeb3();
  const { nodeId } = useAppSelector(state => state.app);
  const {
    data: groupsData,
    isLoading: isLoadingGroups,
    isFetching: isFetchingGroups,
    refetch: refetchGroups,
  } = useFetchProjectGroupsV1Query({ nodeId }, { skip: !nodeId });

  const { data: walletData, isLoading: isLoadingWallet } = useFetchProjectWalletV1Query(
    { nodeId, address: address as string },
    { skip: !nodeId || !address },
  );

  useEffect(() => {
    if (!isLoadingGroups && !isFetchingGroups && groupsData && groupsData.data.length !== 0) {
      const timeouts = groupsData.data.map(group => {
        const timeUntilEnd = new Date(group.startedAt).getTime() - new Date().getTime();

        if (timeUntilEnd > 0) {
          return setTimeout(() => {
            refetchGroups();
          }, timeUntilEnd);
        }

        return null;
      });

      return () => {
        timeouts.forEach((timeout: any) => clearTimeout(timeout));
      };
    }
  }, [isFetchingGroups, isLoadingGroups, groupsData, refetchGroups]);

  const registeredGroups = useMemo(() => {
    if (!isLoadingWallet && walletData && walletData.data) {
      return walletData.data.groups.map(group => group.id);
    }

    return [];
  }, [isLoadingWallet, walletData]);

  const isStarted = useMemo(() => {
    if (!isLoadingGroups && !isFetchingGroups && groupsData && groupsData.data.length !== 0) {
      const diff = groupsData?.data[0]?.startedAt
        ? new Date(groupsData.data[0].startedAt).getTime() - new Date().getTime()
        : 0;
      return diff <= 0;
    }

    return true;
  }, [isFetchingGroups, groupsData]);

  const currentGroup: ProjectGroupV1SchemaType | undefined = useMemo(() => {
    if (!isLoadingGroups && !isFetchingGroups && groupsData && groupsData.data.length !== 0) {
      const now = new Date().getTime();
      return groupsData.data.find(group => {
        return new Date(group.startedAt).getTime() <= now && new Date(group.endedAt).getTime() >= now;
      });
    }

    return undefined;
  }, [groupsData, isLoadingGroups, isFetchingGroups]);

  const isValidMemember = useMemo(() => {
    return isStarted && (!currentGroup || (!isLoadingWallet && registeredGroups.includes(currentGroup.id)));
  }, [isStarted, isLoadingWallet, currentGroup, registeredGroups]);

  return {
    isValidMemember,
    isLoadingGroups,
    isFetchingGroups,
    groupsData,
    currentGroup,
  };
};

export { useProjectGroupV1 };
